import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, from, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Quote } from '../models/quote.model';
import { Blueprint } from '../models/blueprint.model';
import { BlueprintItemWrite } from '../models/blueprint-item.model';
import { QuoteRevision } from '../models/quote-revision.model';
import { DownloadService } from './services/download.service';

export interface QuoteListOptions {
  code_number: string[];
  reference: string[];
  company: string[];
  creator_email: string[];
  created_date: string[];
}

export interface QuoteListResponse {
  count: number;
  results: Quote[];
  options: QuoteListOptions;
}

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  private http = inject(HttpClient);
  private downloadService = inject(DownloadService);

  private blueprintsSubject = new BehaviorSubject<Blueprint[]>([]);
  public blueprints$ = this.blueprintsSubject.asObservable();
  private blueprintsLoaded = false;
  private loadBlueprintsPromise: Promise<Blueprint[]> | null = null;

  getQuotes$(
    showArchived: boolean,
    limit: number,
    offset: number,
    search?: string,
    filters?: { [key: string]: string[] },
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
  ): Observable<QuoteListResponse> {
    let params = new HttpParams()
      .set('show_archived', showArchived.toString())
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (search) {
      params = params.set('search', search);
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key]?.length) {
          params = params.set(key, filters[key].join(','));
        }
      });
    }

    if (sortColumn) {
      params = params.set('sort_column', sortColumn);
      params = params.set('sort_direction', sortDirection || 'asc');
    }

    return this.http.get<QuoteListResponse>('/quotes-api/quotes/', { params });
  }

  deleteQuote$(quoteId: string): Observable<void> {
    return this.http.delete<void>(`/quotes-api/quote/${quoteId}/`);
  }

  patchQuote$(quoteId: string, patch: Partial<Quote>): Observable<Quote> {
    return this.http.patch<Quote>(`/quotes-api/quote/${quoteId}/`, patch);
  }

  createQuote$(): Observable<Quote> {
    return this.http.post<Quote>(`/quotes-api/quotes/`, {});
  }

  /**
   * Load blueprints from the API and update blueprints$.
   * Deduplicates concurrent requests and caches the result unless force is true.
   */
  async loadBlueprints(force = false): Promise<Blueprint[]> {
    if (!force && this.blueprintsLoaded) {
      return this.blueprintsSubject.value;
    }

    if (!force && this.loadBlueprintsPromise) {
      return this.loadBlueprintsPromise;
    }

    const loadPromise = firstValueFrom(
      this.http.get<Blueprint[]>('/quotes-api/blueprints/'),
    )
      .then((blueprints) => {
        if (this.loadBlueprintsPromise !== loadPromise) {
          return blueprints;
        }

        this.blueprintsSubject.next(blueprints);
        this.blueprintsLoaded = true;
        return blueprints;
      })
      .finally(() => {
        if (this.loadBlueprintsPromise === loadPromise) {
          this.loadBlueprintsPromise = null;
        }
      });

    this.loadBlueprintsPromise = loadPromise;
    return loadPromise;
  }

  /**
   * Returns cached blueprints, loading from the API on first use.
   * Pass force=true to bypass the cache (e.g. after mutations).
   */
  getBlueprints$(force = false): Observable<Blueprint[]> {
    return from(this.loadBlueprints(force));
  }

  private invalidateBlueprintsCache(): void {
    this.blueprintsLoaded = false;
    this.loadBlueprintsPromise = null;
  }

  createBlueprint(
    name: string,
    items: BlueprintItemWrite[],
    defaultMaterialId: number | null,
  ): Observable<void> {
    return this.http
      .post<void>(`/quotes-api/blueprints/`, {
        name,
        items,
        default_material_id: defaultMaterialId,
      })
      .pipe(tap(() => this.invalidateBlueprintsCache()));
  }

  updateBlueprint(
    id: string,
    name: string,
    items: BlueprintItemWrite[],
    defaultMaterialId: number | null,
  ): Observable<void> {
    return this.http
      .patch<void>(`/quotes-api/blueprint/${id}/`, {
        name,
        items,
        default_material_id: defaultMaterialId,
      })
      .pipe(tap(() => this.invalidateBlueprintsCache()));
  }

  deleteBlueprint(id: string): Observable<void> {
    return this.http
      .delete<void>(`/quotes-api/blueprint/${id}/`)
      .pipe(tap(() => this.invalidateBlueprintsCache()));
  }

  async downloadPdf(quote: Quote, revision?: QuoteRevision) {
    if (!quote) {
      throw new Error('Quote not loaded');
    }

    // If revision is not provided, use the current revision from the quote
    const revisionToUse = revision || quote.current_revision;

    if (!revisionToUse) {
      throw new Error('Revision not available');
    }

    const url =
      `/quotes-api/quote-revision-items/${quote.id}/` +
      `${revisionToUse.number}/download-pdf/`;

    try {
      await this.downloadService.downloadFile(url, {
        defaultFilename: `${quote.code_number}-${revisionToUse.number}.pdf`,
        mimeType: 'application/pdf',
        openInNewTab: true,
      });
    } catch (error) {
      console.error('Failed to download PDF:', error);
      throw error;
    }
  }
}
